import React, { useState } from "react";
import { leadRepo, staffRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, Card, Input, Kanban, Modal, PageHead, Select, baht, formatDate, useAsync } from "../../components/ui";

const STAGES = [
  { key: "new", label: "ใหม่" },
  { key: "contacted", label: "ติดต่อแล้ว" },
  { key: "qualified", label: "ผ่านการคัดกรอง" },
  { key: "negotiating", label: "กำลังเจรจา" },
  { key: "won", label: "ปิดการขายได้" },
  { key: "lost", label: "เสียโอกาส" },
];

const CHANNELS = ["line", "facebook", "instagram", "walkin", "phone", "website"];
const EMPTY = { name: "", phone: "", channel: "line", source: "Facebook Ads", interestedIn: "", value: 0, note: "" };

export default function LeadBoard() {
  const { orgId, toast } = useApp();
  const { tx, pick } = useI18n();
  const { data: leads, reload } = useAsync(() => leadRepo.all(orgId), [orgId], []);
  const { data: staff } = useAsync(() => staffRepo.all(orgId), [orgId], []);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const columns = STAGES.map((stage) => ({ key: stage.key, label: pick(stage, "label") }));

  const move = async (id, stage) => {
    await leadRepo.update(orgId, id, { stage });
    reload();
  };

  const save = async () => {
    if (!form.name || !form.phone) return toast(tx("กรอกชื่อและเบอร์โทรให้ครบ", "Name and phone required"), "error");
    await leadRepo.create(orgId, { ...form, stage: "new", ownerStaffId: staff?.[0]?.id, nextFollowUpAt: new Date().toISOString() });
    setOpen(false);
    setForm(EMPTY);
    reload();
    toast(tx("เพิ่มลีดใหม่เรียบร้อย", "Lead created"), "success");
  };

  return (
    <>
      <PageHead
        title={tx("กระดานติดตามลีด", "Lead Kanban Board")}
        subtitle={tx("ลากการ์ดเพื่อเปลี่ยนสถานะ", "Drag a card to change stage")}
        actions={<Button variant="primary" onClick={() => setOpen(true)}>+ {tx("เพิ่มลีด", "Add lead")}</Button>}
      />

      <Card>
        <Kanban
          columns={columns}
          items={leads || []}
          groupBy="stage"
          onMove={move}
          renderCard={(lead) => (
            <>
              <h4>{lead.name}</h4>
              <p>{lead.phone} · {lead.source}</p>
              <div className="u-between">
                <Badge tone="indigo">{lead.channel}</Badge>
                <span className="u-strong u-fs-13">{baht(lead.value, 0)}</span>
              </div>
              <div className="u-fs-12 u-muted u-mt-8">{tx("ติดตามต่อ", "Follow up")}: {formatDate(lead.nextFollowUpAt)}</div>
            </>
          )}
        />
      </Card>

      <Modal
        open={open}
        title={tx("เพิ่มลีดใหม่", "New lead")}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{tx("ยกเลิก", "Cancel")}</Button>
            <Button variant="primary" onClick={save}>{tx("บันทึก", "Save")}</Button>
          </>
        }
      >
        <Input label={tx("ชื่อ", "Name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input label={tx("เบอร์โทร", "Phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <Select label={tx("ช่องทาง", "Channel")} options={CHANNELS} value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} />
        <Input label={tx("แหล่งที่มา", "Source")} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
        <Input label={tx("สนใจสินค้า/บริการ", "Interested in")} value={form.interestedIn} onChange={(e) => setForm({ ...form, interestedIn: e.target.value })} />
        <Input label={tx("มูลค่าคาดการณ์", "Est. value")} type="number" value={form.value} onChange={(e) => setForm({ ...form, value: Number(e.target.value) })} />
      </Modal>
    </>
  );
}
